// Client-side cron parser: validates a standard 5-field expression, describes
// it in the page's language, and computes the next run times in the viewer's
// local timezone. No backend — the server ships a static reference table +
// default. Each language words the schedule with its own cron-js-* phrases;
// month and day names and lists come from Intl.
import { lang, locale, t } from "./_i18n.js";
import { toolError, toolUsed } from "./_tool_event.js";

const TOOL = "cron";
// Wait for typing to settle; half-typed input isn't a failed expectation.
const SETTLE_MS = 1_200;
// Shape only. Parser messages quote user tokens, so they can't be reported.
const MAX_FIELDS = 12;
const MONTH_NAMES = { JAN: 1, FEB: 2, MAR: 3, APR: 4, MAY: 5, JUN: 6, JUL: 7, AUG: 8, SEP: 9, OCT: 10, NOV: 11, DEC: 12 };
const DOW_NAMES = { SUN: 0, MON: 1, TUE: 2, WED: 3, THU: 4, FRI: 5, SAT: 6 };
const MACROS = {
  "@hourly": "0 * * * *",
  "@daily": "0 0 * * *",
  "@midnight": "0 0 * * *",
  "@weekly": "0 0 * * 0",
  "@monthly": "0 0 1 * *",
  "@yearly": "0 0 1 1 *",
  "@annually": "0 0 1 1 *",
};

function pad(n) {
  return String(n).padStart(2, "0");
}

// `15th`, `15.`, `15.º`: the language's form for the rank picks the message.
function nth(n) {
  return t("cron-js-nth", { rank: n });
}

let formats;

// The page's list joiner and month and weekday names, built once.
function intl() {
  return (formats ??= {
    list: new Intl.ListFormat(lang(), { type: "conjunction" }),
    month: new Intl.DateTimeFormat(lang(), { month: "long", timeZone: "UTC" }),
    weekday: new Intl.DateTimeFormat(lang(), { weekday: "long", timeZone: "UTC" }),
  });
}

function joinList(items) {
  return intl().list.format(items);
}

// Names in the page's language; 2000-01-02 was a Sunday.
function monthName(n) {
  return intl().month.format(Date.UTC(2000, n - 1, 1));
}

function weekdayName(n) {
  return intl().weekday.format(Date.UTC(2000, 0, 2 + (n % 7)));
}

function upperFirst(text) {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function normalizeNames(spec, nameMap) {
  if (!nameMap) return spec;
  return spec.replace(/[a-zA-Z]+/g, (tok) => {
    const v = nameMap[tok.toUpperCase()];
    if (v === undefined) throw new Error(t("cron-js-error-name", { token: tok }));
    return String(v);
  });
}

function parseField(spec, min, max, nameMap) {
  const normalized = normalizeNames(spec, nameMap);
  const out = new Set();
  for (const part of normalized.split(",")) {
    let m;
    if (part === "*") {
      for (let i = min; i <= max; i++) out.add(i);
    } else if ((m = part.match(/^\*\/(\d+)$/))) {
      const step = +m[1];
      if (step < 1) throw new Error(t("cron-js-error-step"));
      for (let i = min; i <= max; i += step) out.add(i);
    } else if ((m = part.match(/^(\d+)-(\d+)(?:\/(\d+))?$/))) {
      const a = +m[1], b = +m[2], step = m[3] ? +m[3] : 1;
      if (step < 1) throw new Error(t("cron-js-error-step"));
      if (a < min || b > max || a > b) throw new Error(t("cron-js-error-range", { part, min, max }));
      for (let i = a; i <= b; i += step) out.add(i);
    } else if ((m = part.match(/^(\d+)$/))) {
      const v = +m[1];
      if (v < min || v > max) throw new Error(t("cron-js-error-range", { part: v, min, max }));
      out.add(v);
    } else {
      throw new Error(t("cron-js-error-read", { part }));
    }
  }
  if (out.size === 0) throw new Error(t("cron-js-error-empty"));
  return out;
}

function parseCron(expr) {
  let e = expr.trim();
  if (e.startsWith("@")) {
    const mapped = MACROS[e.toLowerCase()];
    if (!mapped) throw new Error(t("cron-js-error-shortcut", { shortcut: e }));
    e = mapped;
  }
  const fields = e.split(/\s+/);
  if (fields.length !== 5) {
    throw new Error(t("cron-js-error-fields", { got: fields.length }));
  }
  const [mi, ho, dm, mo, dw] = fields;
  const minute = parseField(mi, 0, 59, null);
  const hour = parseField(ho, 0, 23, null);
  const dom = parseField(dm, 1, 31, null);
  const month = parseField(mo, 1, 12, MONTH_NAMES);
  const dow = parseField(dw, 0, 7, DOW_NAMES);
  if (dow.has(7)) {
    dow.add(0);
    dow.delete(7);
  }
  return { minute, hour, dom, month, dow, domRestricted: dm !== "*", dowRestricted: dw !== "*", raw: { mi, ho, dm, mo, dw } };
}

function matches(date, f) {
  if (!f.minute.has(date.getMinutes())) return false;
  if (!f.hour.has(date.getHours())) return false;
  if (!f.month.has(date.getMonth() + 1)) return false;
  const domHit = f.dom.has(date.getDate());
  const dowHit = f.dow.has(date.getDay());
  // Cron quirk: when BOTH day-of-month and day-of-week are restricted, a run
  // fires if EITHER matches; otherwise both must match.
  if (f.domRestricted && f.dowRestricted) return domHit || dowHit;
  return domHit && dowHit;
}

function nextRuns(f, count) {
  const runs = [];
  const d = new Date();
  d.setSeconds(0, 0);
  d.setMinutes(d.getMinutes() + 1);
  // ~366 days of minutes: enough to find yearly runs, bounded so an
  // impossible expression (e.g. Feb 30) terminates instead of hanging.
  const cap = 366 * 24 * 60;
  for (let i = 0; i < cap && runs.length < count; i++) {
    if (matches(d, f)) runs.push(new Date(d));
    d.setMinutes(d.getMinutes() + 1);
  }
  return runs;
}

function describeList(spec, name) {
  const label = name || ((n) => String(n));
  const pieces = spec.split(",").map((part) => {
    let m;
    if ((m = part.match(/^(\d+)-(\d+)$/))) return t("cron-js-range", { from: label(+m[1]), to: label(+m[2]) });
    if ((m = part.match(/^(\d+)$/))) return label(+m[1]);
    return part;
  });
  return joinList(pieces);
}

// How each language words the minute and the hour field.
const MINUTE = {
  every: "cron-js-minute-every", step: "cron-js-minute-step", at: "cron-js-minute-at",
  range: "cron-js-minute-range", list: "cron-js-minute-list",
};
const HOUR = {
  every: "cron-js-hour-every", step: "cron-js-hour-step", at: "cron-js-hour-at",
  range: "cron-js-hour-range", list: "cron-js-hour-list",
};

// The minute or hour field as a phrase, worded by `ids`.
function fieldDesc(spec, ids) {
  let m;
  if (spec === "*") return t(ids.every);
  if ((m = spec.match(/^\*\/(\d+)$/))) return t(ids.step, { count: +m[1], n: +m[1], nth: nth(+m[1]) });
  if ((m = spec.match(/^(\d+)$/))) return t(ids.at, { n: +m[1] });
  if ((m = spec.match(/^(\d+)-(\d+)$/))) return t(ids.range, { from: +m[1], to: +m[2] });
  return t(ids.list, { list: describeList(spec, null) });
}

function timeClause(mi, ho) {
  let m;
  if (mi === "*" && ho === "*") return t("cron-js-every-minute");
  if (/^\d+$/.test(mi) && /^\d+$/.test(ho)) return t("cron-js-at-time", { time: `${pad(+ho)}:${pad(+mi)}` });
  if ((m = mi.match(/^\*\/(\d+)$/)) && ho === "*") return t("cron-js-every-minutes", { count: +m[1] });
  if (mi === "0" && (m = ho.match(/^\*\/(\d+)$/))) return t("cron-js-every-hours", { count: +m[1] });
  return t("cron-js-at-past", { minute: fieldDesc(mi, MINUTE), hour: fieldDesc(ho, HOUR) });
}

function describe(raw) {
  const mi = raw.mi;
  const ho = raw.ho;
  const dm = raw.dm;
  const mo = normalizeNames(raw.mo, MONTH_NAMES);
  const dw = normalizeNames(raw.dw, DOW_NAMES);
  const parts = [timeClause(mi, ho)];
  // How many days each field names, so a language can word one day apart
  // from several.
  const domText = dm === "*" ? "" : t("cron-js-on-month-days", { count: parseField(dm, 1, 31, null).size, days: describeList(dm, null) });
  const dowCount = new Set([...parseField(dw, 0, 7, null)].map((d) => d % 7)).size;
  const dowText = dw === "*" ? "" : t("cron-js-on-weekdays", { count: dowCount, days: describeList(dw, weekdayName) });
  if (dm !== "*" && dw !== "*") {
    parts.push(t("cron-js-either", { dom: domText, dow: dowText }));
  } else if (domText) {
    parts.push(domText);
  } else if (dowText) {
    parts.push(dowText);
  }
  if (mo !== "*") parts.push(t("cron-js-in-months", { months: describeList(mo, monthName) }));
  return upperFirst(`${parts.join(", ")}.`);
}

function render(input, descNode, nextList) {
  let f;
  try {
    f = parseCron(input.value);
  } catch (err) {
    input.setAttribute("aria-invalid", "true");
    descNode.textContent = err.message;
    descNode.classList.add("tool-cron__desc--error");
    nextList.replaceChildren();
    return false;
  }
  input.setAttribute("aria-invalid", "false");
  descNode.textContent = describe(f.raw);
  descNode.classList.remove("tool-cron__desc--error");

  const runs = nextRuns(f, 5);
  const fmt = { weekday: "short", year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" };
  const items = runs.length
    ? runs.map((d) => {
        const li = document.createElement("li");
        li.className = "tool-cron__run mk-mono";
        li.textContent = d.toLocaleString(locale(), fmt);
        return li;
      })
    : [Object.assign(document.createElement("li"), { className: "tool-cron__run mk-quiet", textContent: t("cron-js-no-runs") })];
  nextList.replaceChildren(...items);
  return true;
}

function init() {
  const input = document.getElementById("cron-input");
  if (!input) return;
  const descNode = document.getElementById("cron-desc");
  const nextList = document.getElementById("cron-next");
  const presets = document.querySelectorAll(".tool-cron-presets .tool-preset");

  let settle;
  const run = () => {
    const valid = render(input, descNode, nextList);
    const current = input.value.trim();
    for (const b of presets) {
      const on = b.dataset.cron === current;
      b.classList.toggle("is-active", on);
      b.setAttribute("aria-pressed", on);
    }
    return valid;
  };

  input.addEventListener("input", () => {
    const valid = run();
    toolUsed(TOOL, { mode: "expression" });
    clearTimeout(settle);
    if (valid) return;
    settle = setTimeout(() => {
      const expr = input.value.trim();
      if (!expr) return;
      toolError(TOOL, {
        fields: Math.min(expr.split(/\s+/).length, MAX_FIELDS),
        macro: expr.startsWith("@"),
      });
    }, SETTLE_MS);
  });

  for (const b of presets) {
    b.addEventListener("click", () => {
      input.value = b.dataset.cron;
      run();
      clearTimeout(settle);
      toolUsed(TOOL, { mode: "preset", preset: b.dataset.cron });
    });
  }
  run();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
